import { api } from '../services/api';
import { extractMapUrl } from '../utils/mapUtils';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export const fetchContacts = createAsyncThunk(
    'contact/fetchContacts',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/contact');
            const data = response.data;
            const contact = Array.isArray(data) ? data[0] : data;

            if (contact) {
                // Normalize socialMedia data
                let socialMedia = {};
                if (contact.socialMedia) {
                    try {
                        socialMedia = typeof contact.socialMedia === 'string'
                            ? JSON.parse(contact.socialMedia)
                            : contact.socialMedia;
                    } catch (e) {
                    }
                }

                // Ensure all expected fields exist to prevent component-level errors
                return {
                    ...contact,
                    socialMedia: {
                        facebook: socialMedia.facebook || contact.facebook || contact.facebook_url || '',
                        instagram: socialMedia.instagram || contact.instagram || contact.instagram_url || '',
                        youtube: socialMedia.youtube || contact.youtube || contact.youtube_url || '',
                        twitter: socialMedia.twitter || contact.twitter || ''
                    },
                    address: contact.address || '',
                    phone: contact.phone || contact.phone_number || '',
                    email: contact.email || '',
                    mapUrl: extractMapUrl(contact?.mapUrl || contact?.address || '')
                };
            }
            return null;
        } catch (error) {
            return rejectWithValue(error?.message || 'Failed to fetch contact information');
        }
    }
);

export const submitContactMessage = createAsyncThunk(
    'contact/submitMessage',
    async (formData, { rejectWithValue }) => {
        try {
            const response = await api.post('/messages', formData);
            return response?.data;
        } catch (error) {
            return rejectWithValue(error?.message || 'Failed to send message');
        }
    }
);

const contactSlice = createSlice({
    name: 'contact',
    initialState: {
        data: null,
        status: 'idle',
        error: null,
        submitStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
        submitError: null,
    },
    reducers: {
        resetSubmitStatus: (state) => {
            state.submitStatus = 'idle';
            state.submitError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Contacts
            .addCase(fetchContacts.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(fetchContacts.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.data = action.payload;
            })
            .addCase(fetchContacts.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload || action.error.message;
            })
            // Submit Message
            .addCase(submitContactMessage.pending, (state) => {
                state.submitStatus = 'loading';
                state.submitError = null;
            })
            .addCase(submitContactMessage.fulfilled, (state) => {
                state.submitStatus = 'succeeded';
                state.submitError = null;
            })
            .addCase(submitContactMessage.rejected, (state, action) => {
                state.submitStatus = 'failed';
                state.submitError = action.payload || action.error.message;
            });
    },
});

export const { resetSubmitStatus } = contactSlice.actions;

export const selectContactInfo = (state) => state.contact.data;
export const selectContactStatus = (state) => state.contact.status;
export const selectContactError = (state) => state.contact.error;
export const selectSubmitStatus = (state) => state.contact.submitStatus;
export const selectSubmitError = (state) => state.contact.submitError;

export default contactSlice.reducer;
